/**
 * BwaPage — Monats-BWA im Finanzen-Cockpit (Sektion „BWA", Route /finanzen/:firma/bwa)
 *
 * Eingebettet im Mandanten-Cockpit. Liefert eine ruhige, betriebswirtschaftliche
 * Monats-Auswertung pro Firma auf Basis der gebuchten Buchungen (buchungen) und
 * der Kontenstammdaten (buchungskonten).
 *
 * Prop `firmaKey`:
 *   'sv'   → SV Büro   (buchungen.firma = 'sv_buero_koop')
 *   'lcs'  → LCS        (buchungen.firma = 'lcs')
 *   'alle' → Summe beider Firmen
 *
 * DATENBASIS (nur lesen, keine Migration):
 *   - buchungen:        firma, buchung_datum, betrag (+Einnahme/−Ausgabe), netto,
 *                       steuer, buchungskonto_id, status
 *   - buchungskonten:   id, firma, nummer, bezeichnung, typ
 *                       ('erloes'|'aufwand'|'aktiv'|'passiv'|'steuer'|'sonstiges'),
 *                       bwa_gruppe
 *
 * Auswertung:
 *   - Erloese  = Summe Buchungen auf Konten typ='erloes' (positiv dargestellt)
 *   - Aufwand  = gruppiert nach bwa_gruppe (Konten typ='aufwand'), je Gruppe Summe
 *   - Ergebnis = Erloese - Gesamtaufwand
 *   - Spalten:  „Monat" und „kumuliert (Jan-Monat)"
 *
 * RLS/Policies bleiben unangetastet (Inhaber-only).
 * Alle Abfragen sind robust gegen fehlende Tabellen/leere Daten (kein Absturz).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { BarChart3, Loader2, Inbox, Info } from 'lucide-react';

// ============================================================
// Firmen-Mapping (eine Quelle der Wahrheit)
// ============================================================

export type BwaFirmaKey = 'sv' | 'lcs' | 'alle';

interface BwaFirmaConfig {
  key: BwaFirmaKey;
  label: string;
  /** buchungen.firma / buchungskonten.firma — die zu summierenden Firmen-Strings. */
  firmen: string[];
}

const BWA_FIRMEN: Record<BwaFirmaKey, BwaFirmaConfig> = {
  sv: { key: 'sv', label: 'SV Büro', firmen: ['sv_buero_koop'] },
  lcs: { key: 'lcs', label: 'LCS', firmen: ['lcs'] },
  alle: { key: 'alle', label: 'Alle', firmen: ['sv_buero_koop', 'lcs'] },
};

// ============================================================
// Typen
// ============================================================

type KontoTyp = 'erloes' | 'aufwand' | 'aktiv' | 'passiv' | 'steuer' | 'sonstiges';

interface BuchungRow {
  buchung_datum: string;
  betrag: number | string | null;
  buchungskonto_id: string | number | null;
}

interface KontoRow {
  id: string | number;
  typ: KontoTyp | string | null;
  bwa_gruppe: string | null;
}

/** Eine BWA-Zeile (Erloes oder Aufwandsgruppe) mit Monats- und Kumuliert-Wert. */
interface BwaZeile {
  name: string;
  monat: number;       // positiver Betrag (Monat)
  kumuliert: number;   // positiver Betrag (Jan-Monat)
}

// ============================================================
// Helpers
// ============================================================

function fmtEuro(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function isTableNotFound(error: unknown): boolean {
  if (!error) return false;
  const e = error as { message?: string; code?: string };
  const msg = (e.message || '') + ' ' + (e.code || '');
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

function toNum(v: number | string | null | undefined): number {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

// ============================================================
// Daten-Layer
// ============================================================

/** Laedt alle Buchungen der Firma(n) eines Jahres (clientseitige Aggregation). */
async function ladeBuchungen(firmen: string[], jahr: number): Promise<BuchungRow[]> {
  try {
    const von = `${jahr}-01-01`;
    const bis = `${jahr}-12-31`;
    const { data, error } = await supabase
      .from('buchungen')
      .select('buchung_datum, betrag, buchungskonto_id')
      .in('firma', firmen)
      .gte('buchung_datum', von)
      .lte('buchung_datum', bis)
      .limit(50000);
    if (error) throw error;
    return ((data as BuchungRow[]) || []).filter((r) => !!r.buchung_datum);
  } catch (err) {
    if (!isTableNotFound(err)) console.error('BWA: Buchungen laden fehlgeschlagen:', err);
    return [];
  }
}

/** Laedt die Kontenstammdaten der Firma(n). */
async function ladeKonten(firmen: string[]): Promise<KontoRow[]> {
  try {
    const { data, error } = await supabase
      .from('buchungskonten')
      .select('id, typ, bwa_gruppe')
      .in('firma', firmen)
      .limit(20000);
    if (error) throw error;
    return (data as KontoRow[]) || [];
  } catch (err) {
    if (!isTableNotFound(err)) console.error('BWA: Buchungskonten laden fehlgeschlagen:', err);
    return [];
  }
}

// ============================================================
// Auswertung
// ============================================================

interface BwaAuswertung {
  erloeseMonat: number;
  erloeseKumuliert: number;
  aufwand: BwaZeile[];          // je bwa_gruppe, sortiert nach kumuliert desc
  aufwandMonat: number;
  aufwandKumuliert: number;
  ergebnisMonat: number;
  ergebnisKumuliert: number;
}

/**
 * Wertet die Buchungen eines Jahres aus, bezogen auf den gewaehlten Monat
 * (1..12). „Monat" = nur dieser Monat, „kumuliert" = Jan..Monat.
 */
function werteAus(buchungen: BuchungRow[], konten: KontoRow[], monat: number): BwaAuswertung {
  const kontoMap = new Map<string, KontoRow>();
  for (const k of konten) kontoMap.set(String(k.id), k);

  let erloeseMonat = 0;
  let erloeseKumuliert = 0;
  // bwa_gruppe -> { monat, kumuliert }
  const aufwandMap = new Map<string, { monat: number; kumuliert: number }>();

  for (const b of buchungen) {
    const m = Number((b.buchung_datum || '').slice(5, 7));
    if (!(m >= 1 && m <= 12)) continue;
    if (m > monat) continue; // nur bis zum gewaehlten Monat (kumuliert)

    const konto = b.buchungskonto_id != null ? kontoMap.get(String(b.buchungskonto_id)) : undefined;
    const typ = konto?.typ || 'sonstiges';
    const betrag = toNum(b.betrag);
    const istMonat = m === monat;

    if (typ === 'erloes') {
      // Erloese positiv darstellen
      const wert = Math.abs(betrag);
      erloeseKumuliert += wert;
      if (istMonat) erloeseMonat += wert;
    } else if (typ === 'aufwand') {
      const gruppe = (konto?.bwa_gruppe && konto.bwa_gruppe.trim()) || 'Sonstige Kosten';
      const wert = Math.abs(betrag);
      const eintrag = aufwandMap.get(gruppe) || { monat: 0, kumuliert: 0 };
      eintrag.kumuliert += wert;
      if (istMonat) eintrag.monat += wert;
      aufwandMap.set(gruppe, eintrag);
    }
    // aktiv/passiv/steuer/sonstiges fliessen nicht in die BWA ein.
  }

  const aufwand: BwaZeile[] = Array.from(aufwandMap.entries())
    .map(([name, v]) => ({ name, monat: v.monat, kumuliert: v.kumuliert }))
    .sort((a, b) => b.kumuliert - a.kumuliert);

  const aufwandMonat = aufwand.reduce((s, z) => s + z.monat, 0);
  const aufwandKumuliert = aufwand.reduce((s, z) => s + z.kumuliert, 0);

  return {
    erloeseMonat,
    erloeseKumuliert,
    aufwand,
    aufwandMonat,
    aufwandKumuliert,
    ergebnisMonat: erloeseMonat - aufwandMonat,
    ergebnisKumuliert: erloeseKumuliert - aufwandKumuliert,
  };
}

// ============================================================
// UI-Bausteine
// ============================================================

/** Eine Tabellenzeile mit Bezeichnung + Monat + Kumuliert. */
function Zeile({
  label, monat, kumuliert, variant = 'normal',
}: {
  label: string;
  monat: number;
  kumuliert: number;
  variant?: 'normal' | 'kopf' | 'summe' | 'ergebnis';
}) {
  if (variant === 'ergebnis') {
    const positiv = kumuliert >= 0;
    const farbe = positiv ? 'text-emerald-700' : 'text-rose-700';
    return (
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-4 py-3 border-t-2 border-slate-200">
        <span className="text-sm font-bold text-slate-900">{label}</span>
        <span className={`text-sm font-bold tabular-nums text-right w-32 ${monat >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
          {fmtEuro(monat)}
        </span>
        <span className={`text-sm font-bold tabular-nums text-right w-32 ${farbe}`}>
          {fmtEuro(kumuliert)}
        </span>
      </div>
    );
  }

  if (variant === 'summe') {
    return (
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-4 py-2.5 border-t border-slate-100 bg-slate-50/60">
        <span className="text-sm font-semibold text-slate-800">{label}</span>
        <span className="text-sm font-semibold tabular-nums text-right w-32 text-slate-800">{fmtEuro(monat)}</span>
        <span className="text-sm font-semibold tabular-nums text-right w-32 text-slate-800">{fmtEuro(kumuliert)}</span>
      </div>
    );
  }

  if (variant === 'kopf') {
    return (
      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 text-right w-32">Monat</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 text-right w-32">kumuliert</span>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-4 py-2.5 border-t border-slate-50">
      <span className="text-sm text-slate-700 truncate">{label}</span>
      <span className="text-sm tabular-nums text-right w-32 text-slate-600">{fmtEuro(monat)}</span>
      <span className="text-sm tabular-nums text-right w-32 text-slate-600">{fmtEuro(kumuliert)}</span>
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

export default function BwaPage({ firmaKey = 'sv' }: { firmaKey?: BwaFirmaKey }) {
  const cfg = BWA_FIRMEN[firmaKey] || BWA_FIRMEN.sv;

  // Default: aktueller Monat 2026.
  const heute = new Date();
  const defaultJahr = 2026;
  const defaultMonat = heute.getFullYear() === defaultJahr ? heute.getMonth() + 1 : 1;

  const [loading, setLoading] = useState(true);
  const [jahr, setJahr] = useState<number>(defaultJahr);
  const [monat, setMonat] = useState<number>(defaultMonat);
  const [buchungen, setBuchungen] = useState<BuchungRow[]>([]);
  const [konten, setKonten] = useState<KontoRow[]>([]);

  // ── Daten laden (bei Firma- oder Jahreswechsel) ──
  const lade = useCallback(async () => {
    setLoading(true);
    const [b, k] = await Promise.all([
      ladeBuchungen(cfg.firmen, jahr),
      ladeKonten(cfg.firmen),
    ]);
    setBuchungen(b);
    setKonten(k);
    setLoading(false);
  }, [cfg.firmen, jahr]);

  useEffect(() => {
    lade();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaKey, jahr]);

  // ── Auswertung ──
  const a = useMemo(() => werteAus(buchungen, konten, monat), [buchungen, konten, monat]);

  // Buchungen im gewaehlten Monat (fuer Empty-State-Erkennung).
  const hatBuchungenImMonat = useMemo(
    () => buchungen.some((b) => Number((b.buchung_datum || '').slice(5, 7)) === monat),
    [buchungen, monat],
  );

  // Jahres-Optionen: 2026, aktuelles Jahr und Vorjahr.
  const jahrOptionen = useMemo(() => {
    const set = new Set<number>([defaultJahr, heute.getFullYear(), heute.getFullYear() - 1]);
    return Array.from(set).sort((x, y) => y - x);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-5 print:space-y-3">
      {/* Kopf + Monatsauswahl */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">BWA — {cfg.label}</h2>
            <p className="text-xs text-slate-400">Betriebswirtschaftliche Auswertung je Monat</p>
          </div>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <select
            value={monat}
            onChange={(e) => setMonat(Number(e.target.value))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          >
            {MONATE.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={jahr}
            onChange={(e) => setJahr(Number(e.target.value))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          >
            {jahrOptionen.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : !hatBuchungenImMonat ? (
        // ── Empty-State ──
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-6 h-6 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-900">
            Noch keine Buchungen in diesem Monat
          </h3>
          <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">
            Sobald in der Bank gebucht wird, erscheint die BWA hier.
          </p>
          <p className="text-xs text-slate-400 mt-3">
            {MONATE[monat - 1]} {jahr} — {cfg.label}
          </p>
        </div>
      ) : (
        <>
          {/* Ergebnis-Kennzahlen (ruhig, ein Akzent) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Erlöse</div>
              <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{fmtEuro(a.erloeseMonat)}</div>
              <div className="text-xs text-slate-400 mt-0.5">kumuliert {fmtEuro(a.erloeseKumuliert)}</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gesamtkosten</div>
              <div className="text-xl font-bold text-slate-900 mt-1 tabular-nums">{fmtEuro(a.aufwandMonat)}</div>
              <div className="text-xs text-slate-400 mt-0.5">kumuliert {fmtEuro(a.aufwandKumuliert)}</div>
            </div>
            <div className={`rounded-xl p-4 border ${a.ergebnisMonat >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Betriebsergebnis</div>
              <div className={`text-xl font-bold mt-1 tabular-nums ${a.ergebnisMonat >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                {fmtEuro(a.ergebnisMonat)}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">kumuliert {fmtEuro(a.ergebnisKumuliert)}</div>
            </div>
          </div>

          {/* BWA-Tabelle */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900">
                BWA {MONATE[monat - 1]} {jahr}
              </h3>
              <p className="text-xs text-slate-400">
                Spalte „Monat" = {MONATE[monat - 1]}, „kumuliert" = Januar bis {MONATE[monat - 1]}
              </p>
            </div>

            <Zeile label="Position" monat={0} kumuliert={0} variant="kopf" />

            {/* Erloese */}
            <Zeile label="Umsatzerlöse" monat={a.erloeseMonat} kumuliert={a.erloeseKumuliert} />
            <Zeile label="Erlöse gesamt" monat={a.erloeseMonat} kumuliert={a.erloeseKumuliert} variant="summe" />

            {/* Aufwand nach bwa_gruppe */}
            {a.aufwand.length === 0 ? (
              <div className="px-4 py-3 text-xs text-slate-400 border-t border-slate-50">
                Keine Aufwandsbuchungen im Zeitraum.
              </div>
            ) : (
              a.aufwand.map((z) => (
                <Zeile key={z.name} label={z.name} monat={z.monat} kumuliert={z.kumuliert} />
              ))
            )}
            <Zeile label="Gesamtkosten" monat={a.aufwandMonat} kumuliert={a.aufwandKumuliert} variant="summe" />

            {/* Ergebnis */}
            <Zeile
              label="Betriebsergebnis (Erlöse − Kosten)"
              monat={a.ergebnisMonat}
              kumuliert={a.ergebnisKumuliert}
              variant="ergebnis"
            />
          </div>

          {/* Hinweis */}
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>Vorläufig — finale BWA über den Steuerberater.</span>
          </div>
        </>
      )}
    </div>
  );
}
